import { FC, useState } from 'react';
import { useRecPage } from './context';
import { TabKey } from './types';

// Story 2b: Shortlist (n) / Declined (n) tab counts come from the shared
// disposition query in context.tsx (recsysDispositionsForJob).
const buildTabs = (shortlistCount: number, declinedCount: number): { key: TabKey; label: string }[] => [
  { key: 'batch', label: 'Batch Matches' },
  { key: 'search', label: 'Search Pool' },
  { key: 'collab', label: 'Collaborated Search' },
  { key: 'shortlist', label: `Shortlist (${shortlistCount})` },
  { key: 'declined', label: `Declined (${declinedCount})` },
];

// README §Selection panel + tabs: a row with a 1px --border bottom border;
// each tab sits flush on that border with a 2px --brand underline when
// active. The 1px container border + -1px tab margin makes the active tab's
// 2px underline land exactly on (and extend past) the container's border.
const CONTAINER_STYLE: React.CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid #E2E5EA',
  marginBottom: 16,
};

const tabButtonStyle = (active: boolean, hovered: boolean): React.CSSProperties => ({
  appearance: 'none',
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid #EAA827' : '2px solid transparent',
  marginBottom: -1,
  height: 44,
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 4px',
  marginRight: 12,
  fontSize: 14,
  fontWeight: active ? 600 : 500,
  color: active ? '#1F2937' : hovered ? '#1F2937' : '#667085',
  cursor: 'pointer',
  transition: 'color 0.15s ease, border-color 0.15s ease',
});

const TabBar: FC = () => {
  const { view, setView, shortlistCount, declinedCount } = useRecPage();
  const [hoveredKey, setHoveredKey] = useState<TabKey | null>(null);
  const tabs = buildTabs(shortlistCount, declinedCount);

  return (
    <div style={CONTAINER_STYLE} role="tablist" aria-label="Candidate list view">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={view === tab.key}
          style={tabButtonStyle(view === tab.key, hoveredKey === tab.key)}
          onMouseEnter={() => setHoveredKey(tab.key)}
          onMouseLeave={() => setHoveredKey(null)}
          onClick={() => setView(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default TabBar;
